/**
 * Chat Node Types
 *
 * ChatNode is a canvas node that provides conversational AI interaction
 * directly on the whiteboard. Messages are stored in Yjs for real-time
 * collaboration - all users see the same chat history.
 *
 * Key features:
 * - Collaborative message history via Yjs
 * - Streaming AI responses
 * - Pin button on AI messages to create BlockNodes
 * - Output port for streaming to connected BlockNode
 */

// Position type for Flow nodes
type FlowPosition = { x: number; y: number };

// ============================================================================
// MESSAGE TYPES
// ============================================================================

/**
 * Image attachment for chat messages.
 * Images are stored as base64 data URLs for simplicity.
 */
export interface ChatImageAttachment {
  /** Unique attachment ID */
  id: string;

  /** MIME type (e.g., 'image/png', 'image/jpeg') */
  mimeType: string;

  /** Base64-encoded image data (without the data URL prefix) */
  base64: string;

  /** Original filename (for display) */
  filename?: string;

  /** Image dimensions (if known) */
  width?: number;
  height?: number;
}

/**
 * A single chat message.
 * Stored in Yjs array: ydoc.getArray(`chat-${nodeId}`)
 */
export interface CanvasChatMessage {
  /** Unique message ID */
  id: string;

  /** Message role: user or assistant */
  role: 'user' | 'assistant';

  /** Message content (markdown/text) */
  content: string;

  /** Unix timestamp when message was created */
  timestamp: number;

  /** Whether this message has been pinned to canvas as BlockNode */
  isPinned?: boolean;

  /** Image attachments (for multimodal messages) */
  images?: ChatImageAttachment[];
}

// ============================================================================
// STATUS TYPES
// ============================================================================

/**
 * Status of the ChatNode:
 * - idle: Ready for input
 * - streaming: AI is generating a response
 * - error: An error occurred
 */
export type ChatNodeStatus = 'idle' | 'streaming' | 'error';

// ============================================================================
// MODEL SETTINGS TYPES
// ============================================================================

/**
 * Thinking level for models that support extended thinking.
 * Controls how much "thinking time" the model uses before responding.
 */
export type ThinkingLevel = 'none' | 'low' | 'medium' | 'high';

/**
 * Media resolution for image/video inputs.
 * Higher resolution = more tokens but better understanding.
 */
export type MediaResolution = 'auto' | 'low' | 'medium' | 'high';

/**
 * Tool capabilities that can be enabled per chat.
 * Each tool adds specific functionality to the AI.
 */
export interface ChatNodeTools {
  /** Enable structured JSON output mode */
  structuredOutputs: boolean;

  /** Enable code execution in sandbox */
  codeExecution: boolean;

  /** Enable function/tool calling */
  functionCalling: boolean;

  /** Enable web search grounding (Google Search) */
  webSearchGrounding: boolean;

  /** Enable URL context fetching */
  urlContext: boolean;
}

/**
 * Model-specific settings for ChatNode.
 * These settings control how the AI model behaves.
 */
export interface ChatNodeModelSettings {
  /** Selected model ID (e.g., 'claude-sonnet-4-5-20250929') */
  modelId: string;

  /** System instructions - defines AI personality and behavior */
  systemInstructions: string;

  /** Temperature (0-2) - controls randomness. Lower = more deterministic */
  temperature: number;

  /** Media resolution for image/video inputs */
  mediaResolution: MediaResolution;

  /** Thinking level for extended thinking models */
  thinkingLevel: ThinkingLevel;

  /** Tool capabilities */
  tools: ChatNodeTools;
}

/**
 * Create default model settings with sensible defaults.
 */
export function createDefaultModelSettings(): ChatNodeModelSettings {
  return {
    modelId: 'claude-sonnet-4-5-20250929',
    systemInstructions: '',
    temperature: 1,
    mediaResolution: 'auto',
    thinkingLevel: 'none',
    tools: {
      structuredOutputs: false,
      codeExecution: false,
      functionCalling: false,
      webSearchGrounding: false,
      urlContext: false,
    },
  };
}

// ============================================================================
// NODE DATA TYPES
// ============================================================================

/**
 * Data structure for ChatNode instances.
 *
 * Note: Messages are NOT stored in data - they're stored in a Yjs array
 * for real-time collaborative editing: ydoc.getArray(`chat-${nodeId}`)
 */
export interface ChatNodeData {
  // ========== Identity ==========

  /** Node title (optional, displayed in header) */
  title?: string;

  // ========== State ==========

  /** Current node status */
  status: ChatNodeStatus;

  /** Error message if status is 'error' */
  error?: string;

  // ========== Model Settings ==========

  /** Model-specific settings (temperature, tools, etc.) */
  modelSettings?: ChatNodeModelSettings;

  // ========== Runtime Props (injected by BoardCanvas) ==========

  /** Board ID for API calls */
  boardId?: string;

  /** Yjs map of nodes (for creating pinned BlockNodes) */
  nodesMap?: any; // Y.Map<Node>

  /** Yjs map of edges (for creating connections) */
  edgesMap?: any; // Y.Map<Edge>

  /** Yjs document for collaboration and message storage */
  ydoc?: any; // Y.Doc

  /** Wrapper for undo/redo support */
  makeChange?: (fn: () => void) => void;

  /** Callback when title changes */
  onTitleChange?: (title: string) => void;

  /** Callback when status changes */
  onStatusChange?: (status: ChatNodeStatus) => void;

  // Index signature required for React Flow compatibility
  [key: string]: unknown;
}

// ============================================================================
// REACT FLOW NODE TYPE
// ============================================================================

/**
 * React Flow node type for ChatNode.
 */
export type ChatNodeType = {
  id: string;
  type: 'chat';
  data: ChatNodeData;
  position: FlowPosition;
  width?: number;
  height?: number;
  [key: string]: unknown;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create default ChatNodeData with sensible defaults.
 */
export function createDefaultChatNodeData(): ChatNodeData {
  return {
    title: '',
    status: 'idle',
    modelSettings: createDefaultModelSettings(),
  };
}

/**
 * Create a new chat message.
 * @param role - 'user' or 'assistant'
 * @param content - Text content of the message
 * @param images - Optional array of image attachments
 */
export function createChatMessage(
  role: 'user' | 'assistant',
  content: string,
  images?: ChatImageAttachment[]
): CanvasChatMessage {
  return {
    id: crypto.randomUUID(),
    role,
    content,
    timestamp: Date.now(),
    isPinned: false,
    images: images && images.length > 0 ? images : undefined,
  };
}
